<template>
  <div class="main-wrapper">
    <layoutheader></layoutheader>
    <breadcrumb-doctor title="Cancel slots" text="Doctor" text1="Cancel slots" />
    <div class="content">
      <div class="container">
        <div class="row">
          <doctorsidebar></doctorsidebar>
          <div class="col-md-7 col-lg-8 col-xl-9 adm-scope cs">

            <div class="cs-top">
              <p class="adm-muted cs-intro">Pick a day, select free slots to block or bookings to cancel, then confirm. Patients are notified automatically.</p>
              <select v-if="clinics.length > 1" v-model="clinicid" class="adm-select cs-clinic" aria-label="Clinic" @change="onClinic">
                <option v-for="c in clinics" :key="c.clinicid" :value="c.clinicid">{{ c.clinicname }}</option>
              </select>
            </div>

            <div v-if="error" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ error }}</div></div>
            <div v-if="success" class="adm-alert adm-alert--info"><i class="fas fa-check-circle"></i><div>{{ success }}</div></div>

            <div v-if="!clinicsLoading && !clinics.length" class="adm-card adm-empty">
              <i class="fas fa-hospital"></i>
              <strong>No clinic assigned yet</strong>
              Slots appear here once you are assigned to a clinic and slots are generated.
            </div>

            <template v-else>
              <!-- Day strip -->
              <div class="adm-card cs-strip">
                <button type="button" class="adm-icon-btn" aria-label="Earlier days" :disabled="stripStart <= todayIso" @click="shiftStrip(-14)">
                  <i class="fas fa-chevron-left"></i>
                </button>
                <div class="cs-days" role="listbox" aria-label="Day">
                  <button v-for="d in calendar" :key="d.date" type="button" role="option" class="cs-day"
                          :class="{ 'is-active': d.date === day, 'is-empty': !d.free && !d.booked }" :aria-selected="d.date === day"
                          @click="selectDay(d.date)">
                    <span class="cs-day__wd">{{ weekday(d.date) }}</span>
                    <span class="cs-day__num">{{ dayNum(d.date) }}</span>
                    <span class="cs-day__meta">
                      <template v-if="d.free || d.booked">
                        <span v-if="d.free">{{ d.free }} free</span>
                        <span v-if="d.booked" class="cs-day__booked">{{ d.booked }} booked</span>
                      </template>
                      <span v-else>—</span>
                    </span>
                  </button>
                </div>
                <button type="button" class="adm-icon-btn" aria-label="Later days" @click="shiftStrip(14)">
                  <i class="fas fa-chevron-right"></i>
                </button>
              </div>

              <div class="cs-dayhead">
                <h3 class="adm-card__title">{{ longDate(day) }}</h3>
                <div class="adm-toolbar__spacer"></div>
                <button v-if="board && (board.free.length || board.booked.length)" type="button" class="adm-btn adm-btn--sm" @click="selectWholeDay">
                  <i class="fas fa-calendar-times"></i> Select whole day
                </button>
              </div>

              <div v-if="boardLoading" class="adm-card adm-card__body"><div class="adm-skeleton" style="height: 120px"></div></div>
              <div v-else-if="board && !board.free.length && !board.booked.length" class="adm-card adm-empty">
                <i class="fas fa-calendar-check"></i>
                <strong>Nothing to cancel on this day</strong>
                {{ board.has_schedule ? "All of this day's slots have passed or were already blocked." : "No slots were generated for this day." }}
              </div>

              <template v-else-if="board">
                <!-- Booked -->
                <section v-if="board.booked.length" class="adm-card cs-section">
                  <div class="adm-card__header">
                    <div>
                      <h4 class="adm-card__title">Booked <span class="adm-tab__count">{{ board.booked.length }}</span></h4>
                      <p class="adm-card__hint">Cancelling notifies the patient on WhatsApp and email.</p>
                    </div>
                    <div class="adm-toolbar__spacer"></div>
                    <button type="button" class="adm-btn adm-btn--sm adm-btn--ghost" @click="toggleAllBooked">
                      {{ allBookedSelected ? "Clear" : "Select all" }}
                    </button>
                  </div>
                  <label v-for="b in board.booked" :key="b.slno" class="cs-booked" :class="{ 'is-checked': selectedAppts.includes(b.slno) }">
                    <input type="checkbox" :checked="selectedAppts.includes(b.slno)" @change="toggle(selectedAppts, b.slno)" />
                    <span class="cs-time">{{ time12(b.time) }}</span>
                    <span class="adm-avatar adm-avatar--sm">{{ initials(b.patientname || "P") }}</span>
                    <span class="cs-booked__who">
                      <span class="adm-person__name">{{ b.patientname || `Patient ${b.patientid}` }}</span>
                      <span class="adm-person__meta">{{ [b.contactnumbers, b.reason].filter(Boolean).join(" · ") || "No details" }}</span>
                    </span>
                  </label>
                </section>

                <!-- Free -->
                <section v-if="board.free.length" class="adm-card cs-section">
                  <div class="adm-card__header">
                    <div>
                      <h4 class="adm-card__title">Free slots <span class="adm-tab__count">{{ board.free.length }}</span></h4>
                      <p class="adm-card__hint">Blocked slots can no longer be booked by patients.</p>
                    </div>
                  </div>
                  <div class="adm-card__body cs-groups">
                    <div v-for="g in freeGroups" :key="g.label" class="cs-group">
                      <div class="cs-group__head">
                        <span class="cs-group__label"><i :class="g.icon"></i> {{ g.label }}</span>
                        <button type="button" class="adm-btn adm-btn--sm adm-btn--ghost" @click="toggleGroup(g)">
                          {{ g.times.every(t => selectedTimes.includes(t)) ? "Clear" : "Select all" }}
                        </button>
                      </div>
                      <div class="cs-chips">
                        <button v-for="t in g.times" :key="t" type="button" class="cs-chip" :class="{ 'is-checked': selectedTimes.includes(t) }"
                                :aria-pressed="selectedTimes.includes(t)" @click="toggle(selectedTimes, t)">
                          {{ time12(t) }}
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              </template>
            </template>

            <!-- Sticky action bar -->
            <div v-if="selectionCount" class="cs-bar">
              <span>
                <strong>{{ summary }}</strong> selected on {{ shortDate(day) }}
              </span>
              <div class="adm-toolbar__spacer"></div>
              <button type="button" class="adm-btn adm-btn--sm" @click="clearSelection">Clear</button>
              <button type="button" class="adm-btn adm-btn--sm adm-btn--danger" @click="confirming = true">
                <i class="fas fa-ban"></i> {{ selectedAppts.length ? "Cancel & block" : "Block slots" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <admin-modal v-if="confirming" size="sm" :busy="saving"
                 :title="selectedAppts.length ? 'Cancel appointments?' : 'Block slots?'" @close="confirming = false">
      <p class="cs-confirm">
        <b>{{ longDate(day) }}</b>
        <template v-if="selectedTimes.length"><br>{{ selectedTimes.length }} free slot{{ selectedTimes.length === 1 ? "" : "s" }} will be blocked.</template>
        <template v-if="selectedAppts.length"><br>{{ selectedAppts.length }} appointment{{ selectedAppts.length === 1 ? "" : "s" }} will be cancelled and the patient{{ selectedAppts.length === 1 ? "" : "s" }} notified.</template>
      </p>
      <div v-if="selectedAppts.length" class="adm-field">
        <label class="adm-label" for="cs-reason">Reason shown to patients</label>
        <div class="cs-reasons">
          <button v-for="r in REASONS" :key="r" type="button" class="adm-chip cs-reason" :class="{ 'is-on': reason === r }" @click="reason = r">{{ r }}</button>
        </div>
        <input id="cs-reason" v-model="reason" class="adm-input" maxlength="100" placeholder="Doctor unavailable" />
      </div>
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="confirming = false">Keep</button>
        <button type="button" class="adm-btn adm-btn--danger" :disabled="saving" @click="block">
          <span v-if="saving" class="adm-spinner"></span> Confirm
        </button>
      </template>
    </admin-modal>

    <doctorfooter />
  </div>
</template>

<script>
import axios from "axios";
import "../../admin/admin.css";
import AdminModal from "../../admin/components/AdminModal.vue";
import { errorMessage, initials } from "../../admin/admin-utils";

const REASONS = ["Doctor unavailable", "Doctor on leave", "Emergency", "Clinic closed"];

function iso(d) {
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDays(isoDate, n) {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + n);
  return iso(d);
}

export default {
  name: "doctor-cancel-slots",
  components: { AdminModal },
  data() {
    const today = iso(new Date());
    return {
      REASONS,
      todayIso: today,
      clinics: [],
      clinicsLoading: true,
      clinicid: "",
      stripStart: today,
      calendar: [],
      day: today,
      board: null,
      boardLoading: false,
      selectedTimes: [],
      selectedAppts: [],
      confirming: false,
      saving: false,
      reason: REASONS[0],
      error: "",
      success: "",
    };
  },
  computed: {
    doctorId() {
      return sessionStorage.getItem("userdbid");
    },
    selectionCount() {
      return this.selectedTimes.length + this.selectedAppts.length;
    },
    summary() {
      const parts = [];
      if (this.selectedTimes.length) parts.push(`${this.selectedTimes.length} slot${this.selectedTimes.length === 1 ? "" : "s"}`);
      if (this.selectedAppts.length) parts.push(`${this.selectedAppts.length} booking${this.selectedAppts.length === 1 ? "" : "s"}`);
      return parts.join(" + ");
    },
    allBookedSelected() {
      return !!this.board && this.board.booked.every(b => this.selectedAppts.includes(b.slno));
    },
    freeGroups() {
      const groups = [
        { label: "Morning", icon: "fas fa-sun", times: [] },
        { label: "Afternoon", icon: "fas fa-cloud-sun", times: [] },
        { label: "Evening", icon: "fas fa-moon", times: [] },
      ];
      (this.board ? this.board.free : []).forEach(t => {
        const h = Number(t.slice(0, 2));
        groups[h < 12 ? 0 : h < 17 ? 1 : 2].times.push(t);
      });
      return groups.filter(g => g.times.length);
    },
  },
  methods: {
    initials,
    time12(t) {
      const [h, m] = t.split(":").map(Number);
      return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
    },
    weekday(d) {
      return d === this.todayIso ? "Today" : new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short" });
    },
    dayNum(d) {
      return new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    },
    shortDate(d) {
      return new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    },
    longDate(d) {
      return new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    },
    toggle(list, value) {
      const i = list.indexOf(value);
      if (i === -1) list.push(value);
      else list.splice(i, 1);
    },
    toggleGroup(g) {
      const all = g.times.every(t => this.selectedTimes.includes(t));
      this.selectedTimes = all
        ? this.selectedTimes.filter(t => !g.times.includes(t))
        : [...new Set([...this.selectedTimes, ...g.times])];
    },
    toggleAllBooked() {
      this.selectedAppts = this.allBookedSelected ? [] : this.board.booked.map(b => b.slno);
    },
    selectWholeDay() {
      this.selectedTimes = [...this.board.free];
      this.selectedAppts = this.board.booked.map(b => b.slno);
    },
    clearSelection() {
      this.selectedTimes = [];
      this.selectedAppts = [];
    },
    async loadClinics() {
      try {
        const { data } = await axios.get(`/doctors/${this.doctorId}/clinicids`, { params: { details: true } });
        this.clinics = data || [];
        if (this.clinics.length) {
          this.clinicid = await this.busiestClinic();
          await Promise.all([this.loadCalendar(), this.loadDay()]);
        }
      } catch (error) {
        this.error = errorMessage(error, "Could not load your clinics.");
      } finally {
        this.clinicsLoading = false;
      }
    },
    // Open on the clinic with the most upcoming slots, not just the first one.
    async busiestClinic() {
      if (this.clinics.length === 1) return this.clinics[0].clinicid;
      const totals = await Promise.all(this.clinics.map(async c => {
        try {
          const { data } = await axios.get(`/doctors/${this.doctorId}/slot-calendar`, {
            params: { clinicid: c.clinicid, start: this.todayIso, days: 14 },
          });
          return data.reduce((sum, d) => sum + d.free + d.booked, 0);
        } catch (error) {
          return 0;
        }
      }));
      return this.clinics[totals.indexOf(Math.max(...totals))].clinicid;
    },
    async loadCalendar() {
      try {
        const { data } = await axios.get(`/doctors/${this.doctorId}/slot-calendar`, {
          params: { clinicid: this.clinicid, start: this.stripStart, days: 14 },
        });
        this.calendar = data;
      } catch (error) {
        this.error = errorMessage(error, "Could not load your slots.");
      }
    },
    async loadDay() {
      this.boardLoading = true;
      this.clearSelection();
      try {
        const { data } = await axios.get(`/doctors/${this.doctorId}/day-slots`, { params: { clinicid: this.clinicid, date: this.day } });
        this.board = data;
      } catch (error) {
        this.board = null;
        this.error = errorMessage(error, "Could not load this day.");
      } finally {
        this.boardLoading = false;
      }
    },
    onClinic() {
      this.success = "";
      this.loadCalendar();
      this.loadDay();
    },
    selectDay(d) {
      this.day = d;
      this.success = "";
      this.loadDay();
    },
    shiftStrip(n) {
      const next = addDays(this.stripStart, n);
      this.stripStart = next < this.todayIso ? this.todayIso : next;
      this.loadCalendar();
    },
    async block() {
      this.saving = true;
      this.error = "";
      try {
        const { data } = await axios.post(`/doctors/${this.doctorId}/block-slots`, {
          clinicid: this.clinicid,
          date: this.day,
          times: this.selectedTimes,
          appointment_ids: this.selectedAppts,
          reason: this.selectedAppts.length ? this.reason : null,
        });
        const parts = [];
        if (data.removed_slots.length) parts.push(`${data.removed_slots.length} slot${data.removed_slots.length === 1 ? "" : "s"} blocked`);
        if (data.cancelled.length) parts.push(`${data.cancelled.length} appointment${data.cancelled.length === 1 ? "" : "s"} cancelled (patients are being notified)`);
        this.success = `${parts.join(" and ")} on ${this.shortDate(this.day)}.`;
        this.confirming = false;
        await Promise.all([this.loadCalendar(), this.loadDay()]);
      } catch (error) {
        this.error = errorMessage(error, "Could not cancel the selected slots.");
        this.confirming = false;
        if (error.response && error.response.status === 409) this.loadDay();
      } finally {
        this.saving = false;
      }
    },
  },
  created() {
    this.loadClinics();
  },
};
</script>

<style scoped>
.cs {
  font-size: 14px;
  padding-bottom: 72px;
}

.cs-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.cs-intro {
  margin: 0;
  flex: 1 1 300px;
  font-size: 13px;
}

.cs-clinic {
  width: auto;
  min-width: 180px;
  padding: 8px 10px;
}

.cs-strip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
  margin-bottom: 18px;
}

.cs-days {
  flex: 1;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(78px, 1fr);
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: thin;
}

.cs-day {
  border: 1px solid var(--adm-line);
  background: var(--adm-surface);
  border-radius: 10px;
  padding: 8px 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.cs-day:hover {
  border-color: #b2ddff;
}

.cs-day.is-active {
  border-color: var(--adm-primary);
  background: var(--adm-primary-50);
}

.cs-day.is-empty {
  opacity: 0.55;
}

.cs-day__wd {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--adm-muted);
  letter-spacing: 0.03em;
}

.cs-day__num {
  font-size: 14px;
  font-weight: 700;
  color: var(--adm-ink);
}

.cs-day__meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 11px;
  color: var(--adm-muted);
  line-height: 1.3;
}

.cs-day__booked {
  color: var(--adm-warning-700);
  font-weight: 600;
}

.cs-dayhead {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.cs-section {
  margin-bottom: 16px;
}

.cs-booked {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--adm-line-2);
  cursor: pointer;
  margin: 0;
}

.cs-booked:last-child {
  border-bottom: 0;
}

.cs-booked.is-checked {
  background: var(--adm-danger-50);
}

.cs-booked input {
  width: 16px;
  height: 16px;
  accent-color: var(--adm-danger);
}

.cs-booked__who {
  min-width: 0;
}

.cs-time {
  font-weight: 700;
  color: var(--adm-ink);
  min-width: 72px;
  font-variant-numeric: tabular-nums;
}

.cs-groups {
  display: grid;
  gap: 18px;
}

.cs-group__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.cs-group__label {
  font-size: 13px;
  font-weight: 700;
  color: var(--adm-ink-2);
}

.cs-group__label i {
  color: var(--adm-faint);
  margin-right: 6px;
}

.cs-chips {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 8px;
}

.cs-chip {
  border: 1px solid var(--adm-line);
  background: var(--adm-surface);
  border-radius: 8px;
  padding: 8px 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--adm-ink-2);
  cursor: pointer;
  font-variant-numeric: tabular-nums;
  transition: background 0.12s, border-color 0.12s;
}

.cs-chip:hover {
  border-color: #fda29b;
}

.cs-chip.is-checked {
  background: var(--adm-danger-50);
  border-color: var(--adm-danger);
  color: var(--adm-danger-700);
  text-decoration: line-through;
}

.cs-bar {
  position: sticky;
  bottom: 12px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--adm-ink);
  color: #fff;
  border-radius: 12px;
  padding: 10px 12px 10px 18px;
  box-shadow: var(--adm-shadow-lg);
  margin-top: 8px;
  /* keep clear of the site's chat button (fixed bottom-right) */
  margin-right: 72px;
}

.cs-bar .adm-btn:not(.adm-btn--danger) {
  background: transparent;
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
}

.cs-confirm {
  margin: 0 0 14px;
  line-height: 1.6;
}

.cs-reasons {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.cs-reason {
  cursor: pointer;
}

.cs-reason.is-on {
  border-color: var(--adm-primary);
  background: var(--adm-primary-50);
  color: var(--adm-primary-600);
}
</style>
